import { toSuper } from "../../util/string-util.js";
import { Independent } from "./independent.js";
import { Term } from "./term.js";
class MultiplicationExpression {
    multiplier;
    multiplicand;
    static g(multiplier, multiplicand) {
        if (multiplier.isSimple() && multiplicand.isSimple()) {
            return multiplier.mult(multiplicand);
        }
        return new MultiplicationExpression(multiplier, multiplicand);
    }
    _isZero;
    constructor(multiplier, multiplicand) {
        this.multiplier = multiplier;
        this.multiplicand = multiplicand;
        this._isZero = multiplier.isZero() || multiplicand.isZero();
    }
    isZero() {
        return this._isZero;
    }
    isSimple() {
        return false;
    }
    solve(vars) {
        if (this._isZero)
            return 0.0;
        let result = this.multiplier.solve(vars) * this.multiplicand.solve(vars);
        return result;
    }
    toSourceCode() {
        return this.toText(false, "*", false, (pow) => `_p${pow}`);
    }
    toString() {
        return this.toText(false, "∙", false, toSuper, "");
    }
    toText(_forceSign, multiplicationSign, forceNoneInteger, toPower, indent) {
        if (this.isZero())
            return "";
        const exprIndent = indent !== undefined ? indent + "  " : undefined;
        const _indent = indent !== undefined ? indent : "";
        const newLine = indent !== undefined ? "\n" : "";
        return `${_indent}(${newLine}${this.multiplier.toText(false, multiplicationSign, forceNoneInteger, toPower, exprIndent)}${multiplicationSign}${newLine}${this.multiplicand.toText(false, multiplicationSign, forceNoneInteger, toPower, exprIndent)}${newLine}${_indent})`;
    }
    getOperationsHistogram() {
        const histogram = {};
        if (!this.isZero()) {
            let multiplications = 1;
            [this.multiplier, this.multiplicand].forEach((expression) => {
                const eOp = expression.getOperationsHistogram();
                Object.keys(eOp).forEach((opKey) => {
                    if (histogram[opKey] === undefined) {
                        histogram[opKey] = eOp[opKey];
                    }
                    else {
                        histogram[opKey] += eOp[opKey];
                    }
                });
            });
            histogram["*"] =
                histogram["*"] == undefined
                    ? multiplications
                    : histogram["*"] + multiplications;
        }
        return histogram;
    }
    replaceMultiplicand(multiplicand) {
        if (multiplicand.isSimple() && this.multiplier.isSimple()) {
            return this.multiplier.mult(multiplicand);
        }
        this.multiplicand = multiplicand;
        this._isZero = this.multiplier.isZero() || this.multiplicand.isZero();
        return this;
    }
}
class SummationExpression {
    expressions;
    static g(expression0, expression1) {
        if (expression0.isZero())
            return expression1;
        if (expression1.isZero())
            return expression0;
        return new SummationExpression([expression0, expression1]);
    }
    _isZero;
    constructor(expressions) {
        this.expressions = expressions;
        if (this.expressions == undefined || this.expressions.length == 0)
            this._isZero = true;
        let zero = true;
        let i = 0;
        while (zero && i < this.expressions.length) {
            zero = zero && this.expressions[i++].isZero();
        }
        this._isZero = zero;
    }
    isZero() {
        return this._isZero;
    }
    isSimple() {
        return false;
    }
    solve(vars) {
        if (this.expressions == undefined)
            return 0.0;
        let result = 0.0;
        this.expressions.forEach((expression) => {
            result += expression.solve(vars);
        });
        return result;
    }
    toSourceCode() {
        return this.toText(false, "*", false, (pow) => `_p${pow}`);
    }
    toString() {
        return this.toText(false, "∙", false, toSuper, "");
    }
    toText(_forceSign, multiplicationSign, forceNoneInteger, toPower, indent) {
        if (this.isZero())
            return "";
        const exprIndent = indent !== undefined ? indent + "  " : undefined;
        const _exprIndent = indent !== undefined ? indent + "  " : "";
        const _indent = indent !== undefined ? indent : "";
        const newLine = indent !== undefined ? "\n" : "";
        return `${_indent}(${newLine}${this.expressions[0].toText(false, multiplicationSign, forceNoneInteger, toPower, exprIndent)}${newLine}${_exprIndent}+${newLine}${this.expressions[1].toText(false, multiplicationSign, forceNoneInteger, toPower, exprIndent)}${newLine}${_indent})`;
    }
    getOperationsHistogram() {
        const histogram = {};
        let additions = Math.max(0, this.expressions.length - 1);
        Object.values(this.expressions).forEach((expression) => {
            const eOp = expression.getOperationsHistogram();
            Object.keys(eOp).forEach((opKey) => {
                if (histogram[opKey] === undefined) {
                    histogram[opKey] = eOp[opKey];
                }
                else {
                    histogram[opKey] += eOp[opKey];
                }
            });
        });
        if (additions > 0) {
            histogram["+"] =
                histogram["+"] == undefined ? additions : histogram["+"] + additions;
        }
        return histogram;
    }
    getExpression(index) {
        return this.expressions[index];
    }
    replaceExpression(index, expression) {
        this.expressions[index] = expression;
        let zero = true;
        let i = 0;
        while (zero && i < this.expressions.length) {
            zero = zero && this.expressions[i++].isZero();
        }
        this._isZero = zero;
    }
}
export class TermSummationExpression {
    static g(terms) {
        return terms
            ? new TermSummationExpression(terms)
            : new TermSummationExpression();
    }
    terms;
    constructor(expressionOrTerms) {
        this.terms = [];
        if (expressionOrTerms !== undefined) {
            if (expressionOrTerms.length !== undefined) {
                const terms = expressionOrTerms;
                terms.forEach((term) => {
                    if (!term.isZero()) {
                        this.terms.push(term.clone());
                    }
                });
            }
            else {
                const expression = expressionOrTerms;
                expression.terms.forEach((term) => {
                    this.terms.push(term.clone());
                });
            }
            this.normalize();
        }
    }
    clone() {
        return new TermSummationExpression(this);
    }
    isZero() {
        return this.terms.length == 0;
    }
    isConstant() {
        return this.terms.length === 1 && this.terms[0].isConstant();
    }
    isSimple() {
        return this.terms.length === 1;
    }
    normalize() {
        //add all constants together and put at the back
        const terms = this.terms;
        this.terms = [];
        let constantTerm = undefined;
        terms.forEach((term) => {
            if (!term.isZero()) {
                if (term.isConstant()) {
                    if (constantTerm === undefined) {
                        constantTerm = term.clone();
                    }
                    else {
                        constantTerm.addToConstant(term.getConstantValue());
                    }
                }
                else {
                    this.terms.push(term.clone());
                }
            }
        });
        if (constantTerm != undefined) {
            this.terms.push(constantTerm);
        }
    }
    add(that) {
        const thisPlusThat = [];
        this.terms
            .filter((term) => !term.isZero())
            .forEach((term) => thisPlusThat.push(term.clone()));
        that.terms
            .filter((term) => !term.isZero())
            .forEach((term) => thisPlusThat.push(term.clone()));
        return TermSummationExpression.g(thisPlusThat);
    }
    subtract(that) {
        const thisMinusThat = [];
        this.terms
            .filter((term) => !term.isZero())
            .forEach((term) => thisMinusThat.push(term.clone()));
        that.terms
            .filter((term) => !term.isZero())
            .forEach((term) => thisMinusThat.push(term.clone().invert()));
        return TermSummationExpression.g(thisMinusThat);
    }
    mult(that) {
        const thisMitipliedByThat = [];
        if (!that.isZero()) {
            that.terms.forEach((extTerm) => {
                this.terms
                    .map((intTerm) => intTerm.clone().mult(extTerm))
                    .filter((term) => !term.isZero())
                    .forEach((term) => thisMitipliedByThat.push(term));
            });
        }
        return TermSummationExpression.g(thisMitipliedByThat);
    }
    coefficients(independentName) {
        const independentPowerExpressions = {};
        this.terms.forEach((term) => {
            const independent = term.independent(independentName);
            const power = independent == null ? 0 : independent.pow;
            let powerExpression = independentPowerExpressions[power.toString()];
            if (powerExpression == null) {
                powerExpression = TermSummationExpression.g();
                independentPowerExpressions[power.toString()] = powerExpression;
            }
            const coef = term.clone();
            coef.removeIndependent(independentName);
            powerExpression.terms.push(coef);
        });
        const coefficients = {};
        const existingPowers = Object.keys(independentPowerExpressions);
        existingPowers.sort((a, b) => parseInt(a) - parseInt(b));
        const degree = parseInt(existingPowers.at(-1));
        for (let i = 0; i <= degree; i++) {
            const expressionForPower = independentPowerExpressions[i.toString()];
            coefficients[i.toString()] =
                expressionForPower != null
                    ? expressionForPower
                    : TermSummationExpression.g();
        }
        return coefficients;
    }
    toPolynomial(independentName) {
        const independent = Independent.g(independentName);
        const polynomial = TermSummationExpression.g();
        for (let i = 0; i < this.terms.length; i++) {
            const term = this.terms[i].clone();
            if (i > 0) {
                independent.pow = i;
                term.mult(independent);
            }
            if (!term.isZero()) {
                polynomial.terms.push(term);
            }
        }
        return polynomial;
    }
    solve(vars) {
        let result = 0.0;
        this.terms.forEach((term) => {
            result += term.solve(vars);
        });
        return result;
    }
    toSourceCode() {
        return this.toText(false, "*", false, (pow) => `_p${pow}`);
    }
    toString() {
        return this.toText(false, "∙", false, toSuper, "");
    }
    toText(forceSign, multiplicationSign, forceNoneInteger, toPower, indent) {
        const noneZeroTerms = this.terms.filter((term) => !term.isZero());
        const termIndent = indent !== undefined
            ? indent + (noneZeroTerms.length > 1 ? "  " : "")
            : "";
        const _indent = indent !== undefined ? indent : "";
        const newLine = indent !== undefined ? "\n" : "";
        const maxRowLength = indent !== undefined ? Math.max(160 - termIndent.length, 80) : -1;
        let rowLength = 0;
        let sb = noneZeroTerms.length > 0 ? termIndent : "";
        noneZeroTerms.forEach((term) => {
            const termAsString = term.toText(forceSign, true, multiplicationSign, forceNoneInteger, toPower);
            rowLength += termAsString.length;
            if (maxRowLength > 0 && rowLength > maxRowLength) {
                sb += newLine + termIndent;
                rowLength = termAsString.length;
            }
            sb += termAsString;
            forceSign = true;
        });
        return noneZeroTerms.length > 1
            ? `${_indent}(${newLine}${sb}${newLine}${_indent})`
            : `${sb}`;
    }
    getHistogram() {
        const histogram = {};
        for (let it = 0; it < this.terms.length; it++) {
            const term = this.terms[it];
            if (!term.isZero()) {
                const termIndependents = term.independents();
                for (let ii = 0; ii < termIndependents.length; ii++) {
                    const independent = termIndependents[ii];
                    const key = independent.name + "^" + independent.pow.toString();
                    let independentFrequency = histogram[key];
                    if (independentFrequency == undefined) {
                        histogram[key] = { key, independent, frequency: 1 };
                    }
                    else {
                        independentFrequency.frequency++;
                    }
                }
            }
        }
        const orderedHistogram = Object.values(histogram).sort((if0, if1) => {
            const frequencyOrder = if1.frequency - if0.frequency;
            if (frequencyOrder != 0)
                return frequencyOrder;
            return if0.key.localeCompare(if1.key, undefined, { numeric: true });
        });
        return orderedHistogram;
    }
    getOperationsHistogram() {
        const histogram = {};
        let additions = Math.max(0, this.terms.length - 1);
        Object.values(this.terms).forEach((term) => {
            const tOp = term.getOperationsHistogram();
            Object.keys(tOp).forEach((opKey) => {
                if (histogram[opKey] === undefined) {
                    histogram[opKey] = tOp[opKey];
                }
                else {
                    histogram[opKey] += tOp[opKey];
                }
            });
        });
        if (additions > 0) {
            histogram["+"] = additions;
        }
        //order the operations for consistency
        return Object.keys(histogram)
            .sort()
            .reduce((opHist, key) => {
            opHist[key] = histogram[key];
            return opHist;
        }, {});
    }
    optimize(maxDepth = -1, curDepth = 0) {
        if (maxDepth >= 0 && curDepth >= maxDepth)
            return this;
        const histogram = this.getHistogram().filter((entry) => !entry.key.startsWith(Independent.CONSTANT));
        if (histogram.length == 0 || histogram[0].frequency == 1)
            return this;
        if (maxDepth == -1) {
            maxDepth = histogram.length;
        }
        const optimizationResult = this.optimizeForIndependent(histogram[0].independent);
        curDepth++;
        if (curDepth >= maxDepth)
            return optimizationResult.optimized;
        if (optimizationResult.independentTermsExpression.isZero())
            //optimization step was 0% effective
            //should not happen but test any way
            return optimizationResult.optimized;
        const optimizedIndependentTermsExpression = optimizationResult.independentTermsExpression.optimize(maxDepth, curDepth);
        if (optimizationResult.restTermsExpression.isZero()) {
            //optimization step was 100% effective
            if (optimizationResult.optimized
                .replaceMultiplicand) {
                //optimization result consists of a MultiplicationExpression only
                optimizationResult.optimized = (optimizationResult.optimized).replaceMultiplicand(optimizedIndependentTermsExpression);
            }
        }
        else {
            //optimization step was partly effective
            //optimization result consists of a SummationExpression with an embedded MultiplicationExpression
            const optimizedRestTermsExpression = optimizationResult.restTermsExpression.optimize(maxDepth, curDepth);
            const summationExpression = (optimizationResult.optimized);
            if (summationExpression.getExpression(0)
                .replaceMultiplicand) {
                summationExpression.replaceExpression(0, (summationExpression.getExpression(0)).replaceMultiplicand(optimizedIndependentTermsExpression));
            }
            summationExpression.replaceExpression(1, optimizedRestTermsExpression);
        }
        return optimizationResult.optimized;
    }
    optimizeForIndependent(independent) {
        // The key is to extract the independent from all the terms. This will
        // result in two lists of terms: 1) list of terms that did contain the
        // independent; 2) list ofTerms that did not contain the independent.
        // Either of these lists might be empty. When both lists are empty, the
        // starting expression is empty.
        const splitTerms = this.terms.reduce((splittedTerms, term) => {
            //find the independent within the current term
            const independentInTerm = term.independent(independent.name);
            //check the power as well since both need to match
            if (independentInTerm != undefined &&
                independentInTerm.pow === independent.pow) {
                //current term has the independent we are looking for
                //construct a new term without the independent and add it to
                // the list of independentTerms
                const independentTerm = term.clone();
                independentTerm.removeIndependent(independent.name);
                splittedTerms.independentTerms.push(independentTerm);
            }
            else {
                //current term does not have the independent we are looking for so add it to the rest
                splittedTerms.restTerms.push(term);
            }
            return splittedTerms;
        }, {
            independentTerms: [],
            restTerms: [],
        });
        //convert both lists of terms into an expressions
        const independentTermsExpression = TermSummationExpression.g(splitTerms.independentTerms);
        const restTermsExpression = TermSummationExpression.g(splitTerms.restTerms);
        //now construct a new expression of the form INDEPENDENT∙(iTERMS_EXPRESSIONS) + rTERMS_EXPRESSION
        let optimized;
        if (!independentTermsExpression.isZero()) {
            let multiplicationExpression;
            //the independent was found so create the multiplication expression
            //convert the independent into an expression and add to the list of expressions to multiply
            const independentExpression = TermSummationExpression.g([
                Term.g([independent]),
            ]);
            //create the multiplication expression
            if (independentTermsExpression.isSimple()) {
                //the independentTermsExpression consists of a singele term so we can multiply right away
                multiplicationExpression = independentExpression.mult(independentTermsExpression);
            }
            else {
                //create a 'complex' multiplication expression
                multiplicationExpression = MultiplicationExpression.g(independentExpression, independentTermsExpression);
            }
            // if(independentTermsExpression.isConstant()){
            //   //the independent is multiplied by a constant
            //   if(independentExpression.isConstant()){
            //     //the independent is a constant too so multiply them
            //     multiplicationExpression = independentExpression.mult(independentTermsExpression);
            //   }else{
            //     multiplicationExpression = MultiplicationExpression.g(independentTermsExpression,independentExpression)
            //   }
            // }else{
            //   multiplicationExpression = MultiplicationExpression.g(independentExpression,independentTermsExpression);
            // }
            optimized = restTermsExpression.isZero()
                ? multiplicationExpression
                : SummationExpression.g(multiplicationExpression, restTermsExpression);
        }
        else {
            //the independent was not found so the expression could not be optimized;
            optimized = this;
        }
        return { optimized, independentTermsExpression, restTermsExpression };
    }
}
